/**
 * Zustand store for the onboarding wizard (Plan 11; per familie fra FF-11).
 *
 * Holds in-memory state that must survive between steps but must NEVER
 * reach persistent storage (T-data-01: allergy chips are sensitive).
 *
 * Intentionally NOT persisted to localStorage.  A page reload mid-wizard
 * restarts from the server's status: the admin and the members already
 * saved are on the server; only the list shown in this session is lost.
 */
import { create } from 'zustand';
import type { ColorSlot } from '@stell/shared';

export type WizardStep = 1 | 2 | 3;

export type StoredMember = {
  memberId: string;
  name: string;
  role: 'admin' | 'teen' | 'child';
  colorSlot: ColorSlot;
};

type State = {
  step: WizardStep;
  /** Adminen har lagret «Om deg» (egne allergier bekreftet). */
  profileConfirmed: boolean;
  /** Fargen adminen valgte i «Om deg», så medlemssteget ikke tilbyr den. */
  ownColor: ColorSlot | null;
  members: StoredMember[];

  setStep: (step: WizardStep) => void;
  setProfile: (input: { confirmed: boolean; colorSlot?: ColorSlot | null }) => void;
  addMember: (m: StoredMember) => void;
  takenSlots: () => ColorSlot[];
  reset: () => void;
};

const INITIAL: Pick<State, 'step' | 'profileConfirmed' | 'ownColor' | 'members'> = {
  step: 1,
  profileConfirmed: false,
  ownColor: null,
  members: [],
};

export const useOnboardingState = create<State>((set, get) => ({
  ...INITIAL,
  setStep: (step) => set({ step }),
  setProfile: ({ confirmed, colorSlot }) =>
    set((s) => ({ profileConfirmed: confirmed, ownColor: colorSlot === undefined ? s.ownColor : colorSlot })),
  addMember: (m) => set((s) => ({ members: [...s.members, m] })),
  takenSlots: () => {
    const { members, ownColor } = get();
    return [...members.map((m) => m.colorSlot), ...(ownColor ? [ownColor] : [])];
  },
  reset: () => set({ ...INITIAL }),
}));
